'use client';

import { AlertCircle, CheckCircle, InfoCircle } from '@untitledui/icons';
import type { FC } from 'react';

import { cx } from '../../../utils/cx';
import { Avatar } from '../../base/avatar/avatar';
import { Button } from '../../base/buttons/button';
import { CloseButton } from '../../base/buttons/close-button';
import { ProgressBar } from '../../base/progress-indicators/progress-indicators';
import { FeaturedIcon } from '../../foundations/featured-icon/featured-icon';

const iconMap = {
  default: InfoCircle,
  brand: InfoCircle,
  gray: InfoCircle,
  error: AlertCircle,
  warning: AlertCircle,
  success: CheckCircle,
};

interface IconNotificationProps {
  title: string;
  description: string;
  confirmLabel?: string;
  dismissLabel?: string;
  hideDismissLabel?: boolean;
  icon?: FC<{ className?: string }>;
  color?: 'default' | 'brand' | 'gray' | 'error' | 'warning' | 'success';
  progress?: number;
  onClose?: () => void;
  onConfirm?: () => void;
}

export const IconNotification = ({
  title,
  description,
  confirmLabel,
  dismissLabel = 'Dismiss',
  hideDismissLabel,
  icon,
  progress,
  onClose,
  onConfirm,
  color = 'default',
}: IconNotificationProps) => {
  const showProgress = typeof progress === 'number';

  return (
    <div className="gap-4 p-4 relative z-[var(--z-index)] flex max-w-full flex-col rounded-xl bg-primary_alt shadow-lg ring ring-secondary_alt xs:w-[var(--width)] xs:flex-row">
      <FeaturedIcon
        icon={icon || iconMap[color]}
        color={color === 'default' ? 'gray' : color}
        theme={color === 'default' ? 'modern' : 'outline'}
        size="md"
      />

      <div
        className={cx(
          'gap-3 md:pr-8 flex flex-1 flex-col',
          color !== 'default' && 'md:pt-0.5',
          showProgress && 'gap-4',
        )}
      >
        <div className="gap-1 flex flex-col">
          <p className="font-semibold text-sm text-fg-primary">{title}</p>
          <p className="text-sm text-fg-secondary">{description}</p>
        </div>

        {showProgress && (
          <ProgressBar
            labelPosition="bottom"
            value={progress}
            valueFormatter={(value) => `${value}% uploaded...`}
          />
        )}

        <div className="gap-3 flex">
          {!hideDismissLabel && (
            <Button onClick={onClose} size="sm" color="link-gray">
              {dismissLabel}
            </Button>
          )}
          {confirmLabel && (
            <Button onClick={onConfirm} size="sm" color="link-color">
              {confirmLabel}
            </Button>
          )}
        </div>
      </div>

      <div className="top-2 right-2 absolute flex items-center justify-center">
        <CloseButton onClick={onClose} size="sm" label="Dismiss" />
      </div>
    </div>
  );
};

interface AvatarNotificationProps {
  name: string;
  content: string;
  avatar: string;
  date: string;
  confirmLabel: string;
  dismissLabel?: string;
  hideDismissLabel?: boolean;
  icon?: FC<{ className?: string }>;
  color?: 'default' | 'brand' | 'gray' | 'error' | 'warning' | 'success';
  onClose?: () => void;
  onConfirm?: () => void;
}

export const AvatarNotification = ({
  name,
  content,
  avatar,
  confirmLabel,
  dismissLabel = 'Dismiss',
  hideDismissLabel,
  date,
  onClose,
  onConfirm,
}: AvatarNotificationProps) => {
  return (
    <div className="gap-4 p-4 relative z-[var(--z-index)] flex max-w-full flex-col items-start rounded-xl bg-primary_alt shadow-lg ring ring-secondary_alt xs:w-[var(--width)] xs:flex-row">
      <Avatar size="md" src={avatar} alt={name} status="online" />

      <div className="gap-3 pr-8 flex flex-col">
        <div className="gap-1 flex flex-col">
          <div className="gap-2 flex items-center">
            <p className="font-semibold text-sm text-fg-primary">{name}</p>
            <span className="text-sm text-fg-quaternary">{date}</span>
          </div>
          <p className="text-sm text-fg-secondary">{content}</p>
        </div>

        <div className="gap-3 flex">
          {!hideDismissLabel && (
            <Button onClick={onClose} size="sm" color="link-gray">
              {dismissLabel}
            </Button>
          )}
          {confirmLabel && (
            <Button onClick={onConfirm} size="sm" color="link-color">
              {confirmLabel}
            </Button>
          )}
        </div>
      </div>

      <div className="top-2 right-2 absolute flex items-center justify-center">
        <CloseButton onClick={onClose} size="sm" label="Dismiss" />
      </div>
    </div>
  );
};

interface ImageNotificationProps {
  title: string;
  description: string;
  confirmLabel: string;
  dismissLabel?: string;
  hideDismissLabel?: boolean;
  imageMobile: string;
  imageDesktop: string;
  onClose?: () => void;
  onConfirm?: () => void;
}

export const ImageNotification = ({
  title,
  description,
  confirmLabel,
  dismissLabel = 'Dismiss',
  hideDismissLabel,
  imageMobile,
  imageDesktop,
  onClose,
  onConfirm,
}: ImageNotificationProps) => {
  return (
    <div
      style={
        {
          '--width': '496px',
        } as React.CSSProperties
      }
      className="gap-3 p-4 max-md:ring-1 max-md:ring-secondary_alt xs:gap-0 md:p-0 relative z-[var(--z-index)] flex max-w-full flex-col rounded-xl bg-primary_alt shadow-lg xs:w-[var(--width)] xs:flex-row"
    >
      <div className="w-40 md:block -my-px hidden shrink-0 overflow-hidden rounded-l-xl outline-1 -outline-offset-1 outline-black/10">
        <img
          aria-hidden="true"
          src={imageMobile}
          alt="Image Mobile"
          className="t size-full object-cover"
        />
      </div>

      <div className="gap-4 md:gap-3 md:p-4 md:pl-5 md:ring-1 md:ring-secondary_alt flex flex-col rounded-r-xl bg-primary_alt">
        <div className="gap-1 pr-8 flex flex-col">
          <p className="font-semibold text-sm text-fg-primary">{title}</p>
          <p className="text-sm text-fg-secondary">{description}</p>
        </div>

        <div className="h-40 md:hidden w-full overflow-hidden rounded-md bg-secondary">
          <img src={imageDesktop} alt="Image Desktop" className="size-full object-cover" />
        </div>

        <div className="gap-3 flex">
          {!hideDismissLabel && (
            <Button onClick={onClose} size="sm" color="link-gray">
              {dismissLabel}
            </Button>
          )}
          {confirmLabel && (
            <Button onClick={onConfirm} size="sm" color="link-color">
              {confirmLabel}
            </Button>
          )}
        </div>
      </div>

      <div className="top-2 right-2 absolute flex items-center justify-center">
        <CloseButton onClick={onClose} size="sm" label="Dismiss" />
      </div>
    </div>
  );
};
